import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { gsap, SplitText } from '@/lib/gsap';
import { Emoji } from '@/components/Emoji';
import { GlassButton } from '@/components/GlassButton';
import { VOCAB } from '@/lib/data';
import { useAppStore } from '@/lib/store';
import { QUALITY_LABEL, isDue, type Quality } from '@/lib/srs';

type Direction = 'en2zh' | 'zh2en';

export function VocabReviewPage(): JSX.Element {
  const srs = useAppStore((s) => s.srs);
  const review = useAppStore((s) => s.reviewWord);
  const ensure = useAppStore((s) => s.ensureCard);
  // v4: let learner pick the recall direction. EN→ZH (recognition) is easier;
  // ZH→EN (production) is harder but builds active vocabulary faster.
  const [direction, setDirection] = useState<Direction>('en2zh');

  const queue = useMemo(() => {
    const due = VOCAB.filter((w) => {
      const card = srs[w.id];
      return card !== undefined && isDue(card);
    });
    if (due.length > 0) return due;
    return VOCAB.filter((w) => srs[w.id] === undefined).slice(0, 8);
  }, [srs]);

  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [done, setDone] = useState(false);

  if (queue.length === 0 || done) {
    return (
      <div className="text-center py-24">
        <div className="text-7xl mb-6">🎉</div>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold text-white">复习完成</h1>
        <p className="mt-3 text-cream-300">明天 SRS 会再给你推送到期的卡片</p>
        <div className="mt-8 flex gap-3 justify-center">
          <GlassButton to="/app/vocab" size="md" variant="solid">回到单词本</GlassButton>
          <GlassButton to="/app" size="md">返回主页</GlassButton>
        </div>
      </div>
    );
  }

  const word = queue[idx]!;
  const card = srs[word.id];
  if (card === undefined) ensure(word.id);

  function answer(q: Quality): void {
    review(word.id, q);
    if (idx + 1 < queue.length) {
      setIdx(idx + 1);
      setFlipped(false);
    } else {
      setDone(true);
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6" style={{ perspective: '1400px' }}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to="/app/vocab" className="text-sm text-cream-400 hover:text-white">← 退出复习</Link>
        <div className="flex items-center gap-3">
          <DirectionToggle value={direction} onChange={setDirection} />
          <div className="text-xs text-cream-400 font-arcade">{idx + 1} / {queue.length}</div>
        </div>
      </div>

      <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-honey-400 via-mint-400 to-ember-400 transition-all duration-500 ease-out"
          style={{ width: `${((idx + 1) / queue.length) * 100}%` }}
        />
      </div>

      <Flashcard
        key={word.id + direction}
        word={word}
        flipped={flipped}
        direction={direction}
        onFlip={() => setFlipped((f) => !f)}
      />

      {flipped && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {([0, 1, 2, 3] as Quality[]).map((q) => (
            <button
              key={q}
              onClick={() => answer(q)}
              className={`rounded-2xl border border-white/[0.06] bg-white/[0.03] py-4 px-2 hover:bg-white/[0.06] hover:border-white/[0.14] transition-all ${QUALITY_LABEL[q].tone}`}
            >
              <div className="font-display font-bold text-sm sm:text-base">{QUALITY_LABEL[q].label}</div>
              <div className="text-[11px] sm:text-xs text-cream-400 mt-0.5">
                {q === 0 ? '< 1 天' : q === 1 ? '~ 2 天' : q === 2 ? '~ 5 天' : '~ 10 天'}
              </div>
            </button>
          ))}
        </div>
      )}

      {!flipped && (
        <div className="text-center">
          <GlassButton onClick={() => setFlipped(true)} size="md" variant="solid">翻面查看 →</GlassButton>
        </div>
      )}
    </div>
  );
}

function DirectionToggle({ value, onChange }: { value: Direction; onChange: (d: Direction) => void }): JSX.Element {
  return (
    <div className="inline-flex rounded-full bg-ink-800/60 border border-honey-400/20 p-0.5 text-[10px] font-arcade tracking-widest">
      <button
        onClick={() => onChange('en2zh')}
        className={`px-2.5 py-1 rounded-full transition-all ${value === 'en2zh' ? 'bg-honey-400/20 text-honey-200' : 'text-cream-400 hover:text-white'}`}
      >
        EN→ZH
      </button>
      <button
        onClick={() => onChange('zh2en')}
        className={`px-2.5 py-1 rounded-full transition-all ${value === 'zh2en' ? 'bg-ember-400/20 text-ember-200' : 'text-cream-400 hover:text-white'}`}
      >
        ZH→EN
      </button>
    </div>
  );
}

function Flashcard({
  word,
  flipped,
  direction,
  onFlip,
}: {
  word: typeof VOCAB[number];
  flipped: boolean;
  direction: Direction;
  onFlip: () => void;
}): JSX.Element {
  const stageRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const backTextRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const inner = innerRef.current;
    if (inner === null) return;
    gsap.to(inner, { rotateY: flipped ? 180 : 0, duration: 0.9, ease: 'expo.inOut' });
  }, [flipped]);

  useLayoutEffect(() => {
    if (!flipped || backTextRef.current === null) return;
    const split = new SplitText(backTextRef.current, { type: 'chars' });
    const tw = gsap.from(split.chars, {
      opacity: 0, y: 8, stagger: 0.015, duration: 0.4, ease: 'power2.out', delay: 0.45,
    });
    return () => { tw.kill(); split.revert(); };
  }, [flipped]);

  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (stage === null) return;
    gsap.from(stage, { opacity: 0, y: 24, scale: 0.96, duration: 0.55, ease: 'expo.out' });
  }, [word.id]);

  // Front content depends on direction.
  const frontEmoji = word.imageEmoji;
  const frontPrimary = direction === 'en2zh' ? word.en : word.zh;
  const frontMeta = direction === 'en2zh' ? (
    <>
      <div className="mt-2 text-honey-300 font-mono">{word.ipa}</div>
      <div className="mt-1 text-xs text-cream-400 uppercase tracking-wider font-arcade">{word.pos}</div>
    </>
  ) : (
    <div className="mt-2 text-xs text-cream-400 uppercase tracking-wider font-arcade">说出英文单词</div>
  );

  return (
    <div ref={stageRef} style={{ perspective: '1400px' }}>
      <div
        ref={innerRef}
        onClick={onFlip}
        className="relative w-full min-h-[420px] cursor-pointer"
        style={{ transformStyle: 'preserve-3d', transform: 'rotateY(0deg)' }}
      >
        <div
          className="absolute inset-0 rounded-3xl glass-card p-8 md:p-12 flex flex-col items-center justify-center text-center"
          style={{ backfaceVisibility: 'hidden' }}
        >
          <div className="mb-6"><Emoji char={frontEmoji} size={128} /></div>
          <div className={`font-display ${direction === 'en2zh' ? 'text-4xl md:text-5xl font-extrabold text-white' : 'text-3xl md:text-4xl font-bold text-cream-100'}`}>
            {frontPrimary}
          </div>
          {frontMeta}
          <div className="mt-8 text-xs text-cream-400 font-arcade tracking-widest">TAP TO FLIP</div>
        </div>

        <div
          className="absolute inset-0 rounded-3xl glass-card p-8 md:p-12 flex flex-col items-center justify-center text-center"
          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
        >
          <div ref={backTextRef} className="w-full">
            {direction === 'en2zh' ? (
              <>
                <div className="text-3xl text-white font-semibold mb-6">{word.zh}</div>
                <div className="border-t border-white/[0.08] pt-6">
                  <div className="text-sm text-cream-300 mb-1.5 font-mono">{word.exampleEn}</div>
                  <div className="text-sm text-cream-400">{word.exampleZh}</div>
                </div>
              </>
            ) : (
              <>
                <div className="font-display text-4xl md:text-5xl font-extrabold text-white">{word.en}</div>
                <div className="mt-2 text-honey-300 font-mono">{word.ipa}</div>
                <div className="mt-1 text-xs text-cream-400 uppercase tracking-wider font-arcade">{word.pos}</div>
                <div className="mt-6 border-t border-white/[0.08] pt-4">
                  <div className="text-sm text-cream-300 mb-1.5 font-mono">{word.exampleEn}</div>
                  <div className="text-sm text-cream-400">{word.exampleZh}</div>
                </div>
              </>
            )}
          </div>
          <div className="mt-8 text-xs text-cream-400 font-arcade tracking-widest">RATE YOURSELF ↓</div>
        </div>
      </div>
    </div>
  );
}
